import React, { useState, type KeyboardEvent, useRef, Fragment } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'react-intl';
import { renderToString } from 'lib/react';
import { ZoomIn } from '@carbon/icons-react';

// Styles
import cn from 'clsx';
import * as css from './Lightbox.module.scss';

// Lightbox
import Yarl, {
    type ControllerRef as YarlControllerRef,
    type LightboxProps as YarlProps,
    type SlideImage,
    type Plugin,
} from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';

import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

/** @see https://yet-another-react-lightbox.com */
function useConfig(conf?: { thumbs?: boolean; hideArrows?: boolean }) {
    const { formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useRef<YarlControllerRef>(null);

    const toggle = () => setIsOpen(!isOpen);
    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);
    const keyDown = (e: KeyboardEvent) => ([' ', 'Enter'].includes(e.key) ? open() : null);

    const plugins: Plugin[] = [Captions];
    if (conf?.thumbs) plugins.push(Thumbnails);

    return {
        handlers: {
            toggle,
            keyDown,
            open,
            close,
        },
        config: {
            open: isOpen,
            close,
            plugins,
            className: cn(css.yarlRoot, conf?.hideArrows && css.hideArrows),
            labels: {
                Previous: formatMessage({ defaultMessage: 'Previous' }),
                Next: formatMessage({ defaultMessage: 'Next' }),
                Close: formatMessage({ defaultMessage: 'Close' }),
            },
            carousel: {
                finite: true,
                preload: 10,
                padding: '16px',
                spacing: '30%',
                imageFit: 'contain',
                imageProps: {},
            },
            animation: {
                fade: 250,
                swipe: 150,
                easing: {
                    fade: 'ease', // fade-in / fade-out animation timing function
                    swipe: 'ease-out', // slide swipe animation timing function
                    navigation: 'ease-in-out', // slide navigation animation timing function (when using keyboard navigation or navigation buttons)
                },
            },
            captions: {
                descriptionTextAlign: 'start',
                descriptionMaxLines: 3,
            },
            thumbnails: {
                position: 'bottom',
                width: 250,
                height: 150,
                border: 0,
                borderRadius: 0,
                padding: 0,
                gap: 16,
                imageFit: 'contain',
                vignette: false,
            },
            controller: {
                ref,
                aria: false,
                focus: true,
                touchAction: 'none',
                closeOnPullUp: false,
                closeOnPullDown: false,
                closeOnBackdropClick: true,
                preventDefaultWheelY: false,
                preventDefaultWheelX: false,
                disableSwipeNavigation: false,
            },
        } satisfies Partial<YarlProps>,
    };
}

function useImageSource(sources: string[]): string[] {
    const data = useStaticQuery<Queries.AllImagesForLightboxQuery>(graphql`
        query AllImagesForLightbox {
            allFile(filter: { extension: { in: ["jpg", "png", "gif"] } }) {
                edges {
                    node {
                        publicURL
                        relativePath
                    }
                }
            }
        }
    `);

    return sources.map(src => {
        const srcWithoutRelativeSection = src.replace(/^(\.{1,2}\/)*/g, '');
        const matchingNodes = data.allFile.edges.filter(edge => {
            return edge.node.relativePath.endsWith(srcWithoutRelativeSection);
        });

        const count = matchingNodes.length;
        if (count !== 1) {
            console.groupCollapsed(`Could not find image for "${src}"`);
            console.log(`Found ${count} matching nodes:`);
            console.log(`The "src" attribute was transformed to "${srcWithoutRelativeSection}" for matching purpose.`);
            console.log('You might need to rename your file/dir so that this path is unique accross the whole site!');
            console.log('All nodes used for lookup:', data.allFile.edges);
            console.groupEnd();

            throw new Error(
                `Expected exactly one matching image for "${src}", got ${count} - see console for more info`,
            );
        }

        const publicPath = matchingNodes[0].node.publicURL;
        if (!publicPath) throw new Error(`Could not find public path for "${src}".`);

        return publicPath;
    });
}

export type LightboxProps = {
    src: string;
    alt?: string;
    children?: ReactNode;

    shrink?: boolean;
};
export function Lightbox(props: LightboxProps) {
    const { src, alt, children, shrink } = props;

    const { formatMessage } = useIntl();
    const { config, handlers } = useConfig({ hideArrows: true });
    const publicPath: string = useImageSource([src])[0];

    return (
        <Fragment>
            <div className={cn(css.imageWrapperOuter, shrink && css.shrink)}>
                <div className={css.imageWrapperInner}>
                    <img
                        src={publicPath}
                        alt={alt || renderToString(children, 'text')}
                        title={formatMessage({ defaultMessage: 'Click to open full size image' })}
                        // Behavior
                        tabIndex={0}
                        onClick={handlers.open}
                        onKeyDown={handlers.keyDown}
                    />
                    <ZoomIn size="32" className={css.zoomIcon} />
                </div>
            </div>
            <Yarl {...config} slides={[{ src: publicPath }]} />
        </Fragment>
    );
}

export type GalleryProps = {
    children: ReactNode;
    slides: SlideImage[];
};
export function Gallery({ children, slides }: GalleryProps) {
    const { config, handlers } = useConfig({ thumbs: true });

    const urls: string[] = useImageSource(slides.map(x => x.src));
    const $slides = slides.map((slide, i) => ({ ...slide, src: urls[i] }));

    return (
        <Fragment>
            {/* eslint-disable-next-line gatsby/use-gatsby-link */}
            <a
                className={css.gallery}
                onClick={handlers.open}
                children={children}
                // A11Y
                tabIndex={0}
                onKeyDown={handlers.keyDown}
            />
            <Yarl {...config} slides={$slides} />
        </Fragment>
    );
}
